import React, { useContext, useEffect } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Checkbox, CustomModal, Switcher } from '@shared/ui';

import cls from './ModalEdit.module.css';
import { DateInput } from '../../../../components/DateInput/DateInput';
import { Form } from '../../../../components/UI/Form/Form';
import { FormBlock } from '../../../../components/UI/Form/FormBlock/FormBlock';
import { Select } from '../../../../components/UI/Select/Select';
import { SelectIntegration } from '../../../../components/UI/SelectIntegration/SelectIntegration';
import { Input } from '../../../../components/UI/TextInput/Input';
import { Context } from '../../Accounts';

export const AccountEditBody = ({
    account,
    formState,
    onCheckboxChange,
    handleCurrencySelect,
    selectedMonth,
    currentList,
    checkbox,
    formHandler,
    onDateChange,
    selectedLegal,
    selectedEmployee,
    isActiveSwitcher,
    onSwitcherChange,
    selectedIntegration,
    onIntegrationChange,
    onIntegrationAccountChange,
    selectedIntegrationAccount,
    integrationAccountList,
    errors,
    sumFormHandler,
}) => {
    const { t } = useTranslation();
    const bindSelectMapping = {
        personal: (
            <Select
                isSmartPosition
                selected={selectedEmployee || null}
                onChange={handleCurrencySelect}
                placeholder={t('AccountEditBody.return.bindSelectMapping.personal.placeholder')}
                title={t('AccountEditBody.return.bindSelectMapping.personal.title')}
                isRequired
                options={currentList}
                isDisabled
            />
        ),
        legal: (
            <Select
                isSmartPosition
                selected={selectedLegal || null}
                onChange={handleCurrencySelect}
                placeholder={t('AccountEditBody.return.bindSelectMapping.legal.placeholder')}
                title={t('AccountEditBody.return.bindSelectMapping.legal.title')}
                isRequired
                options={currentList}
                isDisabled
            />
        ),
    };

    const { integrationList } = useContext(Context);

    let isLegal = false;

    useEffect(() => {
        if (account.integration) {
            onIntegrationChange(account.integration);
        }
    }, []);

    if (account.user_bind !== null) {
        if (account.user_bind.type === 'legal') {
            isLegal = true;
        }
    }

    let isIntegrationArchive = true;

    if (!account.integration) {
        if (account.is_archive && account.user_bind?.type === 'legal') {
            isIntegrationArchive = false;
        }
    }

    return (
        <CustomModal.Body>
            <div className={classNames(cls.accountBody)}>
                <Form>
                    {!!integrationList.length && isLegal && isIntegrationArchive && (
                        <div className={classNames(cls.switcherWrapper)}>
                            <Switcher
                                classes={cls.switcher}
                                isDisabled={account.integration !== null}
                                title={t('AccountEditBody.return.accountBody.Switcher.title')}
                                isActive={isActiveSwitcher}
                                onClick={onSwitcherChange}
                            />
                        </div>
                    )}
                    {isActiveSwitcher && isLegal && (
                        <FormBlock>
                            <div className={classNames(cls.blockWrapper)}>
                                <Select
                                    isSmartPosition
                                    selected={selectedIntegration || null}
                                    onChange={onIntegrationChange}
                                    placeholder={t(
                                        'AccountEditBody.return.Select.selectedIntegration.placeholder'
                                    )}
                                    title={t(
                                        'AccountEditBody.return.Select.selectedIntegration.title'
                                    )}
                                    isRequired
                                    options={integrationList}
                                    isDisabled={account.integration !== null}
                                    error={errors.integration}
                                />
                            </div>
                            <div className={classNames(cls.blockWrapper)}>
                                <SelectIntegration
                                    isSmartPosition
                                    selected={selectedIntegrationAccount || null}
                                    onChange={onIntegrationAccountChange}
                                    placeholder={t(
                                        'AccountEditBody.return.SelectIntegration.selectedIntegrationAccount.placeholder'
                                    )}
                                    title={t(
                                        'AccountEditBody.return.SelectIntegration.selectedIntegrationAccount.title'
                                    )}
                                    isDisabled={account.integration !== null}
                                    isRequired
                                    options={integrationAccountList.list}
                                    isLoading={integrationAccountList.isLoading}
                                    error={errors.accountInBank}
                                />
                            </div>
                        </FormBlock>
                    )}
                    <FormBlock>
                        {account.user_bind && (
                            <div className={classNames(cls.blockWrapper)}>
                                {bindSelectMapping[account.user_bind.type]}
                            </div>
                        )}
                        <div
                            className={classNames(cls.blockWrapper, {
                                [cls.blockWrapperSingle]: !account.user_bind,
                            })}
                        >
                            <Input
                                title={t('AccountEditBody.return.FormBlock.InputTitle.title')}
                                isRequired
                                value={formState.title}
                                onChange={formHandler}
                                name="title"
                                icon={null}
                                isDisabled={account.is_archive}
                                error={errors.title}
                            />
                        </div>
                    </FormBlock>
                    <FormBlock>
                        <div className={classNames(cls.blockWrapper)}>
                            <Input
                                title={t(
                                    'AccountEditBody.return.FormBlock.InputAccount_number.title'
                                )}
                                isRequired={false}
                                value={formState.account_number}
                                onChange={formHandler}
                                name="account_number"
                                icon={null}
                                isDisabled={account.is_archive || isActiveSwitcher}
                                error={errors.accountNumber}
                            />
                        </div>
                        <div className={classNames(cls.blockWrapper)}>
                            <DateInput
                                title={t(
                                    'AccountEditBody.return.FormBlock.DateInput.purchase_date.title'
                                )}
                                isRequired
                                isDisabled={account.is_archive || isActiveSwitcher}
                                date={formState.date}
                                inventoryGuid="inventoryGuid"
                                parameterName="purchase_date"
                                newCallback={onDateChange}
                                options={{
                                    startDate: formState.date,
                                    singleDatePicker: true,
                                    showDropdowns: true,
                                    autoApply: true,
                                }}
                            />
                        </div>
                    </FormBlock>
                    <FormBlock>
                        <div className={classNames(cls.blockWrapper)}>
                            <Input
                                title={t(
                                    'AccountEditBody.return.FormBlock.InputOpening_balance_value.title'
                                )}
                                isRequired
                                value={formState.opening_balance_value}
                                onChange={sumFormHandler}
                                name="opening_balance_value"
                                icon={null}
                                isDisabled={account.is_archive || isActiveSwitcher}
                                error={errors.accountStartBalance}
                            />
                        </div>
                        <div className={classNames(cls.blockWrapper)}>
                            <Select
                                isSmartPosition
                                selected={selectedMonth || null}
                                onChange={handleCurrencySelect}
                                placeholder={t(
                                    'AccountEditBody.return.FormBlock.SelectSelectedMonth.placeholder'
                                )}
                                title={t(
                                    'AccountEditBody.return.FormBlock.SelectSelectedMonth.title'
                                )}
                                isRequired
                                options={currentList}
                                isDisabled={account.is_archive}
                                error={errors.currency}
                            />
                        </div>
                    </FormBlock>
                    <Checkbox
                        title={t('AccountEditBody.return.Checkbox.title')}
                        isActive={checkbox}
                        onClick={onCheckboxChange}
                        isDisabled={account.default_account || account.is_archive}
                    />
                </Form>
            </div>
        </CustomModal.Body>
    );
};
